import React, { lazy, Suspense } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import NRLoadingPage from './pages/NRLoadingPage/NRLoadingPage';
import { HELPER_CONTENT } from './utils/HelperContent.constants';
import GroupsBreadcrumb from './components/Breadcrums/GroupsBreadcrumb/GroupsBreadcrumb';
import App from './App';
import ApolloProviderWithAuth0 from './ApolloProviderWithAuth0';
import Auth0ProviderWithHistory from './auth/Auth0ProviderWithHistory';
import ReportBreadcrumbElement from './components/Breadcrums/ReportBreadcrumb/ReportBreadcrumb';
import GroupAssetsBreadcrumbElement from './components/Breadcrums/GroupAssetsBreadcrumb/GroupAssetsBreadcrumb';
import NRNavigateRedirect from './NRNavigateRedirect';
import { NRDocumentTitle } from './NRDocumentTitle';
import FeatureFlagRouteWrapper from './components/NotificationsRouteWrapper/FeatureFlagRouteWrapper';
import { FEATURE_FLAGS } from './constants/constants';

const LazySearch = lazy(() => import('./pages/NRSearch/NRSearchContainer'));
const LazyChangePassword = lazy(() => import('./pages/NRChangePasswordForm/NRChangePasswordForm'));
const LazyAssetTable = lazy(() => import('./pages/NRAssetTable/NRAssetTableContainer'));
const LazyGroupsTable = lazy(() => import('./pages/NRGroupsList/NRGroupsList'));
const LazyReport = lazy(() => import('./pages/NRReport/NRReport'));
const LazyUserProfile = lazy(() => import('./pages/NRUserProfile/NRUserProfile'));
const LazySettings = lazy(() => import('./pages/NRSettings/NRSettingsContainer'));
const LazyJiraIntegrationSetup = lazy(() => import('./pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraIntegrationSetupPage'));
const LazyReports = lazy(() => import('./pages/NRReports/NRReportsContainer'));
const LazyDashboard = lazy(() => import('./pages/NRDashboard/NRDashboard'));
const LazyNotFound = lazy(() => import('./pages/NRNotFound/NRNotFound'));
const LazyNotifications = lazy(() => import('./pages/NRNotificationsCenter/NRNotificationsCenterContainer'));

function ElementWrapper({ element }) {
  return <Suspense fallback={<NRLoadingPage />}>{element}</Suspense>;
}

const RedirectToAssetsDefaultTab = () => {
  const { componentId } = useParams();
  return <NRNavigateRedirect to={`/assets/${componentId}/Summary`} replace />;
};
const RedirectToGroupsDefaultTab = () => {
  const { groupId, componentId } = useParams();
  return <NRNavigateRedirect to={`/groups/${groupId}/${componentId}/Summary`} replace />;
};
const RedirectToSearchDefaultTab = () => <NRNavigateRedirect to="/search/Search" replace />;
const RedirectToSettingsDefaultTab = () => <NRNavigateRedirect to="/settings/General" replace />;

const routes = [
  {
    path: '/',
    element: (
      <Auth0ProviderWithHistory>
        <ApolloProviderWithAuth0>
          <NRDocumentTitle />
          <App />
        </ApolloProviderWithAuth0>
      </Auth0ProviderWithHistory>
    ),
    children: [
      {
        index: true,
        element: <ElementWrapper element={<LazyDashboard />} />,
        handle: {
          breadcrumb: 'Overview',
          title: 'Platform Overview - NetRise',
          menu: 'Overview',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.DASHBOARD
        }
      },
      {
        path: 'change-password',
        element: <ElementWrapper element={<LazyChangePassword />} />,
        handle: { breadcrumb: 'Change Password' }
      },
      {
        path: 'assets',
        element: <Outlet />,
        handle: {
          breadcrumb: 'Assets',
          title: 'Assets - NetRise',
          menu: 'Assets',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.ASSETS
        },
        children: [
          {
            index: true,
            element: <ElementWrapper element={<LazyAssetTable />} />
          },
          {
            path: ':componentId',
            element: <RedirectToAssetsDefaultTab />
          },
          {
            path: ':componentId/:tab/:subTab?',
            element: <ElementWrapper element={<LazyReport />} />,
            handle: {
              title: ({ params }) => `Report - ${params.tab} - NetRise`,
              breadcrumb: ReportBreadcrumbElement,
              menu: 'Assets',
              displayHelpDrawer: true,
              helpDrawerContent: HELPER_CONTENT.REPORT
            }
          }
        ]
      },
      {
        path: 'groups',
        element: <Outlet />,
        handle: {
          breadcrumb: 'Groups',
          title: 'Asset Groups - NetRise',
          menu: 'Groups',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.ASSET_GROUPS
        },
        children: [
          {
            index: true,
            element: <ElementWrapper element={<LazyGroupsTable />} />
          },
          {
            path: ':groupId',
            element: <Outlet />,
            handle: {
              breadcrumb: ({ params }) => <GroupsBreadcrumb groupId={params.groupId} />
            },
            children: [
              {
                index: true,
                element: <RedirectToGroupsDefaultTab />
              },
              {
                path: ':componentId',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <RedirectToGroupsDefaultTab />
                  },
                  {
                    path: ':tab/:subTab?',
                    element: <ElementWrapper element={<LazyReport />} />,
                    handle: {
                      title: ({ params }) => `Report - ${params.tab} - NetRise`,
                      breadcrumb: GroupAssetsBreadcrumbElement,
                      menu: 'Groups',
                      displayHelpDrawer: true,
                      helpDrawerContent: HELPER_CONTENT.REPORT
                    }
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: 'reports',
        element: (
          <FeatureFlagRouteWrapper featureFlag={FEATURE_FLAGS.ASSET_DIFFING}>
            <ElementWrapper element={<LazyReports />} />
          </FeatureFlagRouteWrapper>
        ),
        handle: {
          breadcrumb: 'Reports',
          title: 'Reports - NetRise',
          menu: 'Reports'
        }
      },
      {
        path: 'profile',
        element: <ElementWrapper element={<LazyUserProfile />} />,
        handle: {
          breadcrumb: 'My Profile',
          title: 'My Profile - NetRise',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.ORGANIZATION
        }
      },
      {
        path: 'settings',
        element: <Outlet />,
        handle: {
          breadcrumb: 'Settings',
          title: 'Settings - General - NetRise',
          menu: 'Settings',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.ORGANIZATION
        },
        children: [
          { index: true, element: <RedirectToSettingsDefaultTab /> },
          {
            path: 'integrations/jira/setup',
            element: <ElementWrapper element={<LazyJiraIntegrationSetup />} />,
            handle: {
              breadcrumb: 'Jira Setup',
              title: 'Settings - Jira Setup - NetRise',
              menu: 'Settings',
              displayHelpDrawer: true,
              helpDrawerContent: HELPER_CONTENT.ORGANIZATION
            }
          },
          {
            path: 'integrations/jira/configuration',
            element: <ElementWrapper element={<LazySettings />} />,
            handle: {
              breadcrumb: 'Configuration',
              title: 'Settings - Jira Configuration - NetRise',
              menu: 'Settings',
              displayHelpDrawer: true,
              helpDrawerContent: HELPER_CONTENT.ORGANIZATION
            }
          },
          {
            path: ':tab',
            element: <ElementWrapper element={<LazySettings />} />,
            handle: {
              title: ({ params }) => {
                if (params?.tab === 'User') return 'Settings - Users & Access - NetRise';
                if (params?.tab === 'UsersAndAccess') return 'Settings - Users & Access - NetRise';
                if (params?.tab === 'integrations') return 'Settings - Integrations - NetRise';
                return `Settings - ${params?.tab} - NetRise`;
              }
            }
          }
        ]
      },
      {
        path: 'search',
        element: <Outlet />,
        handle: {
          breadcrumb: 'Search',
          title: 'Search - NetRise',
          menu: 'Search',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.SEARCH
        },
        children: [
          { index: true, element: <RedirectToSearchDefaultTab /> },
          {
            path: ':tab',
            element: <ElementWrapper element={<LazySearch />} />,
            handle: {
              title: ({ params }) => {
                return `${params?.tab === 'Search' ? '' : 'Trace '}Search - NetRise`;
              }
            }
          }
        ]
      },
      {
        path: '*',
        element: <ElementWrapper element={<LazyNotFound />} />,
        handle: {
          breadcrumb: 'Error',
          title: '404 Page Not Found - NetRise',
          menu: 'Error',
          displayHelpDrawer: false
        }
      },
      {
        path: 'notifications-center',
        // TODO: Remove this wrapper once notifications feature is fully implemented - [TURB-5126]
        element: (
          <ElementWrapper
            element={
              <FeatureFlagRouteWrapper featureFlag={FEATURE_FLAGS.NOTIFICATIONS}>
                <LazyNotifications />
              </FeatureFlagRouteWrapper>
            }
          />
        ),
        handle: {
          breadcrumb: 'Notifications Center',
          title: 'Notifications Center - NetRise',
          displayHelpDrawer: true,
          helpDrawerContent: HELPER_CONTENT.NOTIFICATIONS
        }
      }
    ]
  }
];

export default routes;
